<template :id="id">
  <ag-drawer :id="id" :ref="id" @close="close">
    <template slot="title">
      Documents
    </template>
    <template slot="content">
      <div class="flex flex-col">
        <div class="pb-5 flex flex-gap-5">
          <button
            class="py-2 px-6 rounded-full text-sm focus:outline-none"
            :class="[
              activeFilter === FILTERS.REQUIRED
                ? 'bg-blue-600 text-white'
                : 'border border-gray-300'
            ]"
            @click="setDefaultFilter(FILTERS.REQUIRED)"
          >
            Required ({{ documents.length }})
          </button>
          <button
            class="ml-4 py-2 px-6 rounded-full text-sm focus:outline-none"
            :class="[
              activeFilter === FILTERS.PUBLISHED
                ? 'bg-blue-600 text-white'
                : 'border border-gray-300'
            ]"
            @click="setDefaultFilter(FILTERS.PUBLISHED)"
          >
            Published ({{ published_documents.length }})
          </button>
        </div>
        <div class="" v-if="activeFilter === FILTERS.REQUIRED">
          <div class="flex items-center justify-between relative">
            <label for="due_date_only">Show checklist with due date only</label>
            <input
              type="checkbox"
              v-model="dueDateOnly"
              id="due_date_only"
              class="h-0 w-0 opacity-0"
            />
            <div
              class="py-2 absolute right-0 h-4 w-10 bg-gray-300 rounded-full flex items-center"
              @click="toggleDueDateOnly"
            >
              <div
                class="bg-gray-600 rounded-full h-5 w-5"
                :class="{ 'ml-auto': dueDateOnly }"
              />
            </div>
          </div>
          <div class="mt-5 bg-white rounded" v-if="documents.length">
            <div class="py-6 px-8 flex">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 12.75V16.5H1.5V2.25H15.8535C15.9192 2.24989 15.9838 2.26706 16.0408 2.29979C16.0979 2.33252 16.1453 2.37966 16.1783 2.43648C16.2113 2.4933 16.2289 2.55781 16.2291 2.62355C16.2294 2.68928 16.2124 2.75392 16.1798 2.811L13.5 7.5L16.1798 12.189C16.2124 12.2461 16.2294 12.3107 16.2291 12.3765C16.2289 12.4422 16.2113 12.5067 16.1783 12.5635C16.1453 12.6203 16.0979 12.6675 16.0408 12.7002C15.9838 12.7329 15.9192 12.7501 15.8535 12.75H3ZM3 3.75V11.25H13.9155L11.772 7.5L13.9155 3.75H3Z"
                  fill="#ED8936"
                />
              </svg>
              <div class="ml-3">
                <p class="text-gray-600 text-xs">Stage</p>
                <p class="text-gray-800 font-medium text-sm">
                  {{ application.current_stage_name }}
                </p>
              </div>
            </div>
            <div class="px-4 md:px-8 pt-6 pb-8 border-t border-gray-200 document-checklist-docs-container">
              <document-card
                v-for="document in documents"
                :key="document.id"
                :document="document"
              />
            </div>
          </div>
          <no-documents v-else class="mt-40">
            <div
              class="mt-4 w-2/3 text-center flex items-center justify-center flex-col text-gray-600"
            >
              <p>
                Looks like there are no required documents
                <span v-if="dueDateOnly">with due date</span>
              </p>
            </div>
          </no-documents>
        </div>
        <div class="" v-else>
          <div
            class="bg-white rounded shadow published-docs-container"
            v-if="published_documents.length"
          >
            <published-document
              v-for="document in published_documents"
              :key="document.id"
              :document="document"
            />
          </div>
          <no-documents v-else class="mt-40">
            <div
              class="mt-4 w-2/3 text-center flex items-center justify-center flex-col text-gray-600"
            >
              <p>
                Looks like there are no published documents
              </p>
            </div>
          </no-documents>
        </div>
      </div>
    </template>
  </ag-drawer>
</template>

<script>
const FILTERS = {
  REQUIRED: 'required',
  PUBLISHED: 'published'
};

import { uniqueKey } from '../../../utils';
import AgDrawer from '@/components/AgDrawer.vue';
import axios from 'axios';
import moment from 'moment';
import DocumentCard from './DocumentCard';
import PublishedDocument from './PublishedDocument';
import NoDocuments from '@/components/NoDocuments.vue';

const ONE_WEEK = 7;

export default {
  name: 'AgDocumentChecklistDrawer',
  components: {
    AgDrawer,
    DocumentCard,
    NoDocuments,
    PublishedDocument
  },
  props: {
    application: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      id: uniqueKey(),
      activeFilter: FILTERS.REQUIRED,
      FILTERS,
      dueDateOnly: false,
      documents: [],
      published_documents: []
    };
  },
  created() {
    this.fetchChecklist();
    this.fetchPublishedDocumets();
  },
  watch: {
    dueDateOnly() {
      this.fetchChecklist();
    }
  },
  methods: {
    fetchChecklist() {
      axios
        .get(`/api/applications/${this.application.id}/document-checklists`, {
          params: {
            'due-only': this.dueDateOnly ? 'true' : null
          }
        })
        .then(({ data }) => {
          this.documents = data.data;
        });
    },
    fetchPublishedDocumets() {
      axios
        .get(`/api/applications/${this.application.id}/published-documents`)
        .then(({ data }) => {
          this.published_documents = data.data;
        });
    },
    close() {
      this.$emit('close');
    },
    isDueSoon(due_date) {
      if (!due_date) {
        return false;
      }

      const due_date_moment = moment(due_date.date);
      const today = moment();
      const days = due_date_moment.diff(today, 'days');
      if (days < ONE_WEEK && days >= 0) {
        return true;
      }
      return false;
    },
    formatDate(due_date) {
      if (!due_date) {
        return 'N/A';
      }
      return moment(due_date.date).format('D MMM, YYYY');
    },
    setDefaultFilter(filter) {
      this.activeFilter = filter;
    },
    toggleDueDateOnly() {
      this.dueDateOnly = !this.dueDateOnly;
    }
  }
};
</script>

<style lang="scss" scoped>
.document-checklist-docs-container {
  max-height: calc(100vh - 18.5rem);
  overflow-y: auto;
}
.published-docs-container {
  max-height: calc(100vh - 10rem);
  overflow-y: auto;
}
</style>
